import React from 'react';

import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {
  RegistrationTableBase,
  RegistrationTableBaseProps,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {convertRegistrationTableColumnsAndRowsToTableProps} from 'notebook/registration-tables/RegistrationTableUtil';
import {
  LegacyStructuredTableRow,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {StageRun} from 'workflows/WorkflowConstants';

/**
 * Exposes some utilities for sharing between RegistrationTable and RegistrationTableConfig. This is part
 * of the project to unify these two similar components.
 */

export interface RegistrationTableUtilitiesProps {
  utils: {
    getRegistrationTableData: ({
      newColumns,
      newRows,
      labelingStrategy,
    }: {
      newColumns?: Array<StructuredTableColumn>;
      newRows?: Array<LegacyStructuredTableRow>;
      labelingStrategy?: RegistryLabelingStrategy;
    }) => RegistrationTableBase;

    mergeRowsAndColumnsIntoTable: (
      index: number,
      registrationTable: RegistrationTableBase,
      stageRuns: Array<StageRun>,
      tableOp?: TableOpDiffMap
    ) => void;
  };
}

export default function withRegistrationTableUtils<P extends RegistrationTableBaseProps>(
  WrappedComponent: React.ComponentClass<RegistrationTableBaseProps & RegistrationTableUtilitiesProps>
): React.FunctionComponent<P> {
  function RegistrationTableUtilsWrapper(props: RegistrationTableBaseProps): JSX.Element {
    function getRegistrationTableData({
      newColumns,
      newRows,
      labelingStrategy,
    }: {
      newColumns?: Array<StructuredTableColumn>;
      newRows?: Array<LegacyStructuredTableRow>;
      labelingStrategy?: RegistryLabelingStrategy;
    } = {}): RegistrationTableBase {
      return {
        name: props.tableName,
        labelingStrategy: labelingStrategy || props.labelingStrategy,
        tagSchemaId: props.tagSchema.id,
        tagSchema: props.tagSchema,
        batchSchema: props.batchSchema,
        tableType: props.tableType,
        provenanceUuid: props.provenanceUuid,
        columns: newColumns || props.structuredTableColumns,
        rows: newRows || props.rows,
        isWatchingInputTable: props.isWatchingInputTable,
        cid: props.cid,
      };
    }

    function mergeRowsAndColumnsIntoTable(
      index: number,
      registrationTable: RegistrationTableBase,
      stageRuns: Array<StageRun>,
      tableOp?: TableOpDiffMap
    ): void {
      const {columnConstraints, columnLabels, table, rowData, metadata, widths} =
        convertRegistrationTableColumnsAndRowsToTableProps({registrationTable, stageRuns});

      props.updateHandler(
        index,
        {
          table: {
            ...props.table,
            widths,
            columnConstraints,
            columnLabels,
            table,
            rowData,
            metadata,
          },
        },
        undefined,
        tableOp
      );
    }

    const utils = {
      getRegistrationTableData,
      mergeRowsAndColumnsIntoTable,
    };
    return <WrappedComponent {...props} utils={utils} />;
  }

  return RegistrationTableUtilsWrapper;
}
